import { useEffect, useState } from 'react';
import { Trash2, Plus, Calendar, ChevronUp, ChevronDown } from 'lucide-react';
import type { ProjectSubtask } from '@t3/shared';
import { ApiError, apiFetch } from '../api/client.ts';
import { Button } from '../components/Button.tsx';

interface Props {
  projectId: string;
  subtasks: ProjectSubtask[];
  onChange: () => Promise<void> | void;
}

interface Template {
  id: string;
  name: string;
  itemCount: number;
}

export function SubtaskList({ projectId, subtasks, onChange }: Props): React.ReactElement {
  const [newTitle, setNewTitle] = useState('');
  const [newDue, setNewDue] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [chosenTemplate, setChosenTemplate] = useState('');

  useEffect(() => {
    apiFetch<{ templates: Template[] }>('/api/subtask-templates')
      .then((r) => setTemplates(r.templates))
      .catch(() => setTemplates([]));
  }, []);

  async function applyTemplate() {
    if (!chosenTemplate) return;
    if (!confirm(`Add the items from this template to the project?`)) return;
    setBusy(true);
    setErr(null);
    try {
      await apiFetch(`/api/subtask-templates/${chosenTemplate}/apply-to-project/${projectId}`, {
        method: 'POST',
      });
      setChosenTemplate('');
      await onChange();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Apply failed.');
    } finally {
      setBusy(false);
    }
  }

  const total = subtasks.length;
  const done = subtasks.filter((s) => s.completedAt !== null).length;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  async function add() {
    if (!newTitle.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      await apiFetch(`/api/projects/${projectId}/subtasks`, {
        method: 'POST',
        json: {
          title: newTitle.trim(),
          dueDate: newDue || null,
        },
      });
      setNewTitle('');
      setNewDue('');
      await onChange();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Failed to add subtask.');
    } finally {
      setBusy(false);
    }
  }

  async function toggle(subtask: ProjectSubtask) {
    setBusy(true);
    setErr(null);
    try {
      await apiFetch(`/api/projects/${projectId}/subtasks/${subtask.id}/completion`, {
        method: 'POST',
        json: { completed: subtask.completedAt === null },
      });
      await onChange();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Failed to update subtask.');
    } finally {
      setBusy(false);
    }
  }

  async function reorder(index: number, direction: -1 | 1) {
    const target = index + direction;
    if (target < 0 || target >= subtasks.length) return;
    const a = subtasks[index]!;
    const b = subtasks[target]!;
    setBusy(true);
    setErr(null);
    try {
      // swap their sort_order values
      await Promise.all([
        apiFetch(`/api/projects/${projectId}/subtasks/${a.id}`, {
          method: 'PATCH',
          json: { sortOrder: b.sortOrder },
        }),
        apiFetch(`/api/projects/${projectId}/subtasks/${b.id}`, {
          method: 'PATCH',
          json: { sortOrder: a.sortOrder },
        }),
      ]);
      await onChange();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Failed to reorder.');
    } finally {
      setBusy(false);
    }
  }

  async function remove(subtask: ProjectSubtask) {
    if (!confirm(`Delete subtask "${subtask.title}"?`)) return;
    setBusy(true);
    setErr(null);
    try {
      await apiFetch(`/api/projects/${projectId}/subtasks/${subtask.id}`, { method: 'DELETE' });
      await onChange();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Failed to delete subtask.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between">
        <span className="text-sm text-[var(--color-muted)]">
          {done} of {total} complete
        </span>
        <span className="text-sm font-semibold text-[var(--color-header)]">{pct}%</span>
      </div>
      <div className="mt-1 h-2 overflow-hidden rounded-full bg-[var(--color-border)]">
        <div
          className="h-full bg-[var(--color-t3-blue)] transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>

      {subtasks.length === 0 ? (
        <p className="mt-4 text-sm text-[var(--color-muted)]">No subtasks yet.</p>
      ) : (
        <ul className="mt-4 space-y-1">
          {subtasks.map((s, i) => {
            const overdue =
              s.completedAt === null &&
              s.dueDate !== null &&
              new Date(s.dueDate) < new Date(new Date().toDateString());
            return (
              <li
                key={s.id}
                className="group flex items-center gap-2 rounded-[var(--radius-button)] px-2 py-1.5 hover:bg-[var(--color-surface)]"
              >
                <div className="flex flex-col text-[var(--color-muted)]">
                  <button
                    type="button"
                    onClick={() => void reorder(i, -1)}
                    disabled={busy || i === 0}
                    className="disabled:opacity-30 hover:text-[var(--color-t3-blue)]"
                    title="Move up"
                  >
                    <ChevronUp className="h-3 w-3" />
                  </button>
                  <button
                    type="button"
                    onClick={() => void reorder(i, 1)}
                    disabled={busy || i === subtasks.length - 1}
                    className="disabled:opacity-30 hover:text-[var(--color-t3-blue)]"
                    title="Move down"
                  >
                    <ChevronDown className="h-3 w-3" />
                  </button>
                </div>
                <input
                  type="checkbox"
                  checked={s.completedAt !== null}
                  onChange={() => void toggle(s)}
                  disabled={busy}
                  className="h-4 w-4 cursor-pointer accent-[var(--color-t3-blue)]"
                />
                <span
                  className={
                    'flex-1 text-sm ' +
                    (s.completedAt
                      ? 'text-[var(--color-muted)] line-through'
                      : 'text-[var(--color-body)]')
                  }
                >
                  {s.title}
                </span>
                {s.dueDate && (
                  <span
                    className={
                      'inline-flex items-center gap-1 text-xs ' +
                      (overdue ? 'text-[var(--color-danger)]' : 'text-[var(--color-muted)]')
                    }
                  >
                    <Calendar className="h-3 w-3" />
                    {s.dueDate}
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => void remove(s)}
                  disabled={busy}
                  className="opacity-0 transition group-hover:opacity-100 hover:text-[var(--color-danger)]"
                  title="Delete"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <div className="mt-4 flex gap-2">
        <input
          type="text"
          placeholder="Add a subtask…"
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void add();
          }}
          disabled={busy}
          className="flex-1 rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-2 text-sm outline-none focus:border-[var(--color-t3-blue)] focus:ring-2 focus:ring-[var(--color-t3-blue)]/30"
        />
        <input
          type="date"
          value={newDue}
          onChange={(e) => setNewDue(e.target.value)}
          disabled={busy}
          title="Optional due date"
          className="rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-2 text-sm outline-none focus:border-[var(--color-t3-blue)] focus:ring-2 focus:ring-[var(--color-t3-blue)]/30"
        />
        <Button onClick={() => void add()} disabled={busy || !newTitle.trim()}>
          <Plus className="h-4 w-4" />
        </Button>
      </div>
      {err && <p className="mt-2 text-sm text-[var(--color-danger)]">{err}</p>}

      {templates.length > 0 && (
        <div className="mt-4 flex items-end gap-2 border-t border-[var(--color-border)] pt-3">
          <select
            value={chosenTemplate}
            onChange={(e) => setChosenTemplate(e.target.value)}
            disabled={busy}
            className="flex-1 rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-2 text-sm"
          >
            <option value="">Apply a template…</option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} ({t.itemCount} items)
              </option>
            ))}
          </select>
          <Button onClick={() => void applyTemplate()} disabled={busy || !chosenTemplate}>
            Apply
          </Button>
        </div>
      )}
    </div>
  );
}
